import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { Icoon } from '../ui/Icoon';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDagen, fmtDag, fmtDuur, fmtUren, isoWeek, maandagVan, sprintBelasting, weekdagIndex, type DagKleur, type Kalender } from '@ep/core';

type SprintMap = ReturnType<typeof sprintBelasting>;
import { api } from '../lib/api';
import { useData, useMag, useTaakOpenen, type Data, type EngineerRij, type TaakRij } from '../lib/data';
import { toast } from '../ui/toast';
import { Laden, MultiSelect, Veld } from '../ui/ui';

const KLEUREN = ['#0B2375', '#C2410C', '#7C3AED', '#15803D', '#BE185D', '#A16207', '#1D4ED8', '#B91C1C'];
function projectKleur(sleutel: string) {
  let h = 0;
  for (const c of sleutel) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return KLEUREN[h % KLEUREN.length];
}
const DOEL_TEKST: Record<DagKleur, string> = { ruimte: '⇩ hier', vol: '⇩ vol', overvol: '⇩ overvol', afwezig: '⇩ afwezig', geenwerkdag: '⇩ geen werkdag' };

interface Verplaatsing { taakId: string; titel: string; vorige: { engineerId: string | null; startdatum: string | null; dagen: number } }

export function Weekrooster() {
  const { data: d, kal } = useData();
  const mag = useMag();
  const qc = useQueryClient();
  const nav = useNavigate();
  const taak = useTaakOpenen();
  const [week, setWeek] = useState<string | null>(null);
  const [tweeWeken, setTweeWeken] = useState(false);
  const [weekend, setWeekend] = useState(false);
  const [klant, setKlant] = useState<string[]>([]);
  const [proj, setProj] = useState<string[]>([]);
  const [eng, setEng] = useState<string[]>([]);
  const [exp, setExp] = useState<string[]>([]);
  const [afwFilter, setAfwFilter] = useState<'' | 'afwezig' | 'aanwezig'>('');
  const [sleep, setSleep] = useState<TaakRij | null>(null);
  const [laatste, setLaatste] = useState<Verplaatsing | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const kanVerplaatsen = mag('taken.plannen');

  const ma = week ?? (d ? maandagVan(d.vandaag) : '');
  const dagen = useMemo(() => {
    const lijst: string[] = [];
    if (!ma) return lijst;
    for (let i = 0; i < (tweeWeken ? 14 : 7); i++) {
      const x = addDagen(ma, i);
      if (weekend || weekdagIndex(x) <= 4) lijst.push(x);
    }
    return lijst;
  }, [ma, tweeWeken, weekend]);

  const engineers = useMemo(() => {
    if (!d || !kal) return [];
    return d.engineers
      .filter((e) => e.actief !== false)
      .filter((e) => !eng.length || eng.includes(e.id))
      .filter((e) => !exp.length || e.expertises.some((x) => exp.includes(x.naam)))
      .filter((e) => {
        if (!afwFilter) return true;
        const afw = dagen.some((x) => kal.afwezigOp(e, x));
        return afwFilter === 'afwezig' ? afw : !afw;
      });
  }, [d, kal, eng, exp, afwFilter, dagen]);
  // SPEC §9.7: sprintwerk (geschat) per engineer per dag, ter info
  const sprintMap: SprintMap = useMemo(
    () => (d && kal ? sprintBelasting({ kal, sprints: d.sprints ?? [], werkpakketten: d.werkpakketten, items: d.takenpakketten, vandaag: d.vandaag }) : new Map()),
    [d, kal],
  );

  if (!d || !kal) return <Laden />;

  const toonTaak = (t: TaakRij) => (!klant.length || klant.includes(t.klant ?? '')) && (!proj.length || proj.includes(t.projectId ?? ''));

  const verplaats = async (t: TaakRij, engineerId: string, startdatum: string, dagenAantal?: number, stil = false) => {
    // direct tonen (optimistisch), daarna opslaan
    const oud = qc.getQueryData<Data>(['data']);
    if (oud) {
      const e = kal.engineer(engineerId)!;
      const lijst = kal.planDagen(e, startdatum, dagenAantal ?? t.dagen);
      qc.setQueryData<Data>(['data'], { ...oud, taken: oud.taken.map((x) => (x.id === t.id ? { ...x, engineerId, startdatum, einddatum: lijst[lijst.length - 1], sync: 'NODIG' } : x)) });
    }
    try {
      const r = await api.post<{ vorige: Verplaatsing['vorige'] }>(`/api/taken/${t.id}/verplaats`, { engineerId, startdatum, dagen: dagenAantal });
      if (!stil) {
        setLaatste({ taakId: t.id, titel: t.titel, vorige: r.vorige });
        toast(`Verplaatst naar ${d.engineers.find((e) => e.id === engineerId)?.naam}, ${fmtDag(startdatum)}. Outlook volgt binnen een paar minuten.`, 'succes');
      }
    } catch (e) {
      toast((e as Error).message, 'fout');
    } finally {
      await qc.invalidateQueries({ queryKey: ['data'] });
    }
  };

  const onStart = (e: DragStartEvent) => setSleep(d.taken.find((t) => t.id === String(e.active.id).split('@')[0]) ?? null);
  const onEind = (e: DragEndEvent) => {
    const t = sleep;
    setSleep(null);
    if (!t || !e.over) return;
    const [engineerId, datum] = String(e.over.id).split('|');
    // Verschuif de taak zodanig dat de dag waarop je hem pakte op de doeldag valt
    const pakDag = (e.active.data.current as { dag?: string } | undefined)?.dag ?? t.startdatum!;
    const verschil = Math.round((Date.parse(datum) - Date.parse(pakDag)) / 86400000);
    const nieuweStart = addDagen(t.startdatum!, verschil);
    if (engineerId === t.engineerId && nieuweStart === t.startdatum) return;
    verplaats(t, engineerId, nieuweStart);
  };
  const ongedaan = async () => {
    if (!laatste?.vorige.engineerId || !laatste.vorige.startdatum) return;
    const t = d.taken.find((x) => x.id === laatste.taakId);
    if (!t) return;
    await verplaats(t, laatste.vorige.engineerId, laatste.vorige.startdatum, laatste.vorige.dagen, true);
    toast('Laatste verplaatsing is ongedaan gemaakt', 'succes');
    setLaatste(null);
  };

  const kolommen = `150px repeat(${dagen.length}, minmax(${tweeWeken ? 90 : 130}px, 1fr))`;
  return (
    <div className="pagina kolom" style={{ gap: 10 }}>
      <div className="paneel kolom" style={{ gap: 10 }}>
        <div className="rij wrap">
          <button className="knop" onClick={() => setWeek(addDagen(ma, -7))}>‹</button>
          <button className="knop" onClick={() => setWeek(null)}>Deze week</button>
          <button className="knop" onClick={() => setWeek(addDagen(ma, 7))}>›</button>
          <h2>
            Week {isoWeek(ma)}{tweeWeken ? `–${isoWeek(addDagen(ma, 7))}` : ''}: {fmtDag(dagen[0], 'datum')} t/m {fmtDag(dagen[dagen.length - 1], 'jaar')}
          </h2>
          <div className="groei" />
          <select value={afwFilter} onChange={(e) => setAfwFilter(e.target.value as typeof afwFilter)} style={{ width: 190 }}>
            <option value="">Filter verlof/ziek</option>
            <option value="afwezig">Alleen wie afwezig is</option>
            <option value="aanwezig">Alleen wie aanwezig is</option>
          </select>
          <button className={`knop ${tweeWeken ? 'aan' : ''}`} onClick={() => setTweeWeken(!tweeWeken)}><Icoon naam={tweeWeken ? 'square-check' : 'square'} maat={16} /> 2 weken</button>
          <button className={`knop ${weekend ? 'aan' : ''}`} onClick={() => setWeekend(!weekend)}><Icoon naam={weekend ? 'square-check' : 'square'} maat={16} /> Weekend tonen</button>
          <button className="knop" onClick={() => { setKlant([]); setProj([]); setEng([]); setExp([]); setAfwFilter(''); }}><Icoon naam="x" maat={16} /> Filters wissen</button>
        </div>
        <div className="grid4">
          <Veld label="Klant (meerdere mogelijk)"><MultiSelect opties={[...new Set(d.taken.map((t) => t.klant).filter(Boolean) as string[])].sort().map((x) => ({ waarde: x, label: x }))} waarden={klant} onChange={setKlant} placeholder="Alle klanten" /></Veld>
          <Veld label="Project"><MultiSelect opties={d.projecten.map((p) => ({ waarde: p.id, label: `${p.nummer} ${p.naam}` }))} waarden={proj} onChange={setProj} placeholder="Alle projecten" /></Veld>
          <Veld label="Engineer"><MultiSelect opties={d.engineers.map((e) => ({ waarde: e.id, label: e.naam }))} waarden={eng} onChange={setEng} placeholder="Alle engineers" /></Veld>
          <Veld label="Expertise"><MultiSelect opties={d.expertises.map((x) => ({ waarde: x.naam, label: x.naam }))} waarden={exp} onChange={setExp} placeholder="Alle expertises" /></Veld>
        </div>
        <div className="rij">
          <span className="klein" style={{ color: sleep ? 'var(--oranje)' : 'var(--mute)', fontWeight: sleep ? 600 : 400 }}>
            {sleep
              ? `✥ ${sleep.projectnummer ?? ''} ${sleep.titel} – laat los op de dag en engineer waar de taak heen moet (groen = ruimte, geel = vol, rood = overvol, grijs = geen werkdag/afwezig).`
              : `Klik op een taak om die te openen.${kanVerplaatsen ? ' Sleep een taak naar een andere dag of engineer om hem te verplaatsen.' : ''} Vak per dag: groen = ruimte, geel = vol, rood = overvol, paars = verlof/ziek, grijs = geen werkdag.`}
          </span>
          <div className="groei" />
          {laatste && kanVerplaatsen && (
            <button className="knop" style={{ borderColor: 'var(--oranje)' }} onClick={ongedaan} title={`Zet "${laatste.titel}" terug`}>
              ↶ Ongedaan maken
            </button>
          )}
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={onStart} onDragEnd={onEind} onDragCancel={() => setSleep(null)}>
        <div className="wk-grid" style={{ gridTemplateColumns: kolommen }}>
          <div />
          {dagen.map((x) => (
            <div key={x} className={`wk-kopcel ${x === d.vandaag ? 'vandaag' : ''}`}>
              {fmtDag(x, tweeWeken ? 'kort' : 'lang')}
              {x === d.vandaag ? ' (vandaag)' : ''}
            </div>
          ))}
          <div />
          {dagen.map((x) => <TeamBalk key={x} kal={kal} dag={x} />)}
          {engineers.map((e) => (
            <EngineerRij key={e.id} e={e} dagen={dagen} kal={kal} d={d} sprint={sprintMap.get(e.id)} toonTaak={toonTaak} sleep={sleep} kanVerplaatsen={kanVerplaatsen} open={(t) => taak.open(`/taken/${t.id}`)} />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>{sleep ? <BlokInhoud t={sleep} overlay /> : null}</DragOverlay>
      </DndContext>
      {!engineers.length && <div className="paneel leeg">Geen engineers in deze selectie.</div>}
    </div>
  );
}

function TeamBalk({ kal, dag }: { kal: Kalender; dag: string }) {
  const engs = kal.engineers.filter((e) => e.actief !== false && e.inzet !== 'NIET_CAPACITEIT');
  let vrij = 0;
  let metRuimte = 0;
  let metCap = 0;
  for (const e of engs) {
    if (kal.capaciteit(e, dag) <= 0) continue;
    metCap++;
    const v = kal.vrij(e, dag);
    if (v > 0) {
      vrij += v;
      metRuimte++;
    } else vrij += v;
  }
  const kleur = metCap === 0 ? 'var(--mute)' : vrij < 0 ? 'var(--rood)' : metRuimte === 0 ? 'var(--geel)' : 'var(--groen)';
  const tekst = metCap === 0 ? 'Geen werkdag' : vrij < 0 ? `Overvol: ${fmtUren(-vrij)}u te veel` : metRuimte === 0 ? 'Iedereen vol' : `Ruimte: ${fmtUren(vrij)}u vrij (${metRuimte}/${metCap})`;
  return (
    <div className="wk-team" style={{ background: kleur, color: kleur === 'var(--geel)' ? 'var(--ink)' : '#fff' }}>
      {tekst}
    </div>
  );
}

function EngineerRij({ e, dagen, kal, d, sprint, toonTaak, sleep, kanVerplaatsen, open }: { e: EngineerRij; dagen: string[]; kal: Kalender; d: Data; sprint?: ReturnType<SprintMap['get']>; toonTaak: (t: TaakRij) => boolean; sleep: TaakRij | null; kanVerplaatsen: boolean; open: (t: TaakRij) => void }) {
  const totaal = dagen.reduce((s, x) => s + kal.gepland(e, x), 0);
  const cap = dagen.reduce((s, x) => s + kal.capaciteit(e, x), 0);
  const sp = dagen.reduce((s, x) => s + (sprint?.get(x)?.uren ?? 0), 0);
  return (
    <>
      <div className="wk-eng">
        <div className="vet">{e.naam}</div>
        <div className="klein" style={{ color: 'var(--accent-tekst)', marginTop: 4 }}>
          {fmtUren(totaal)} / {fmtUren(cap)} uur gepland
        </div>
        {sp > 0 && <div className="klein" style={{ color: 'var(--blauw)' }} title="Geschat sprintwerk: open items van de engineer plus een deel van de items zonder eigenaar">+ ~{fmtUren(sp)}u sprintwerk</div>}
        <div className="klein mute">{e.expertises.map((x) => x.naam).join(', ')}</div>
      </div>
      {dagen.map((x) => (
        <DagCel key={x} e={e} dag={x} kal={kal} d={d} sprint={sprint?.get(x)} toonTaak={toonTaak} sleep={sleep} kanVerplaatsen={kanVerplaatsen} open={open} />
      ))}
    </>
  );
}

function DagCel({ e, dag, kal, d, sprint, toonTaak, sleep, kanVerplaatsen, open }: { e: EngineerRij; dag: string; kal: Kalender; d: Data; sprint?: { uren: number; label: string }; toonTaak: (t: TaakRij) => boolean; sleep: TaakRij | null; kanVerplaatsen: boolean; open: (t: TaakRij) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${e.id}|${dag}`, disabled: !kanVerplaatsen });
  const kleur = kal.dagKleur(e, dag);
  const afw = kal.afwezigOp(e, dag);
  const taken = (kal.takenOp(e.id, dag) as TaakRij[]).filter(toonTaak).sort((a, b) => (a.van ?? '').localeCompare(b.van ?? ''));
  const info = afw
    ? AFW[afw.soort] ?? 'Afwezig'
    : !kal.isWerkdag(e, dag, false)
      ? kal.isHeleFeestdag(dag)
        ? d.feestdagen.find((f) => f.datum === dag)?.naam ?? 'Feestdag'
        : 'Geen werkdag'
      : `${fmtUren(kal.gepland(e, dag))}u gepland · ${fmtUren(kal.bezetUren(e, dag))}u bezet`;
  return (
    <div ref={setNodeRef} className={`wk-cel ${kleur} ${sleep ? 'doel' : ''} ${isOver ? 'over' : ''}`} title={sleep ? `Hier neerzetten bij ${e.naam} op ${fmtDag(dag)}` : undefined}>
      <div className="info">{info}</div>
      {dag <= d.vandaag && (d.geschreven?.[e.id]?.[dag] ?? 0) > 0 && (
        <div className="klein" style={{ color: 'var(--accent-tekst)', padding: '0 2px' }} title="Geschreven uren (weekstaat, timer, agenda)">
          ✍ {fmtDuur(d.geschreven![e.id]![dag])} geschreven
        </div>
      )}
      {sprint && dag >= d.vandaag && (
        <div className="klein" style={{ background: 'var(--blauw-bg)', color: 'var(--blauw)', borderRadius: 4, padding: '1px 5px', margin: '2px 0' }} title="Geschat sprintwerk (telt niet in de vrije uren; zet focusblokken aan om de agenda echt vrij te houden)">
          🏃 {sprint.label} · ~{fmtUren(sprint.uren)}u
        </div>
      )}
      {taken.map((t) => (
        <Blok key={t.id} t={t} dag={dag} kan={kanVerplaatsen} open={open} />
      ))}
      {sleep && <div className="klein vet" style={{ marginTop: 'auto', textAlign: 'center', color: 'var(--ink)', opacity: 0.75 }}>{DOEL_TEKST[kleur]}</div>}
    </div>
  );
}

const AFW: Record<string, string> = { VERLOF: '🏖️ Verlof', ZIEK: '🤒 Ziek', CURSUS: '🎓 Cursus', OVERIG: 'Afwezig' };

function Blok({ t, dag, kan, open }: { t: TaakRij; dag: string; kan: boolean; open: (t: TaakRij) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${t.id}@${dag}`, data: { dag }, disabled: !kan || t.bron === 'AGENDA' });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} onClick={() => open(t)} className={isDragging ? 'sleept' : ''} style={{ outline: 'none' }}>
      <BlokInhoud t={t} />
    </div>
  );
}

function BlokInhoud({ t, overlay }: { t: TaakRij; overlay?: boolean }) {
  const k = projectKleur(t.projectnummer ?? t.changeNr ?? t.titel);
  return (
    <div className={`wk-blok ${overlay ? 'overlay' : ''}`} style={{ borderLeftColor: k, background: `${k}26`, width: overlay ? 220 : undefined }} title={`${t.van ?? ''}–${t.tot ?? ''} ${t.projectnummer ?? t.changeNr ?? ''}\n${t.klant ?? ''} · ${t.titel}`}>
      <div className="t">
        {t.van ? `${t.van}–${t.tot} ` : ''}
        {t.projectnummer ?? t.changeNr ?? ''}
        {t.sync === 'NODIG' ? ' ⏳' : ''}
        {t.sync === 'FOUT' ? ' ⚠️' : ''}
      </div>
      <div className="s">
        {t.werkvorm === 'ONSITE' && t.reisMin ? `🚗${Math.round(t.reisMin)}m · ` : ''}
        {t.taakType === 'OVERLEG' ? '💬 ' : t.taakType === 'FOCUS' ? '🎯 ' : ''}
        {t.klant ? `${t.klant} · ` : ''}
        {t.titel}
      </div>
    </div>
  );
}
